import React, { useEffect, useState } from 'react';
import Table, { TFilter } from '@components/table';
import styles from './styles.module.scss';
import Status from '@components/common/status';
import PriceStatus from '@components/common/price_status';
import Typography from '@components/common/typography';
import RangeDatePicker from '@components/range_date_picker';
import Button from '@components/common/button';
import Drawer from '@components/drawer';
import Input from '@components/common/input';
import Accordion from '@components/common/accordin';
import MultiSelect from '@components/common/multi_select';
import Range from '@components/range';
import { useDebounceValue } from 'usehooks-ts';
import Dropdown from '@components/common/dropdown';
import { useGiftCards } from '@hooks/api/useGiftCards';
import moment from 'moment';
import {
    EGiftCardStatus,
    EPaymentIntentStatus,
    ETransactionType,
    TAdminGiftCard,
    TGetAdminGiftCardsFilter,
    TPaymentIntent,
    TPaymentIntentsFilter
} from '@xeppt/xeppt-sdk/types';
import {
    getGiftCardsStatusColor,
    getGiftCardStatusString,
    getIntentStatusVariant,
    getPaymentIntentStatusString,
    getTransactionAction,
    getTransactionMethod,
    getTransactionStatus
} from '@utils/index';
import Checkbox from '@components/common/checkbox';
import { useIsFirstRender } from '@mantine/hooks';
import { useExternalPayments } from '@hooks/api/useExternalPayments';
import Textarea from '@components/common/textarea';

const giftCardStatuses = [
    {
        label: 'Issued',
        value: EGiftCardStatus.ISSUED
    },
    {
        label: 'Pending',
        value: EGiftCardStatus.PENDING
    },
    {
        label: 'Active',
        value: EGiftCardStatus.ACTIVE
    },
    {
        label: 'Redeemed',
        value: EGiftCardStatus.REDEEMED
    },
    {
        label: 'Partially redeemed',
        value: EGiftCardStatus.PARTIALLY_REDEEMED
    },
    {
        label: 'Cancel requested',
        value: EGiftCardStatus.CANCEL_REQUESTED
    },
    {
        label: 'Cancelled',
        value: EGiftCardStatus.CANCELLED
    }
];

const ExternalPaymentsLayout = () => {
    const isFirstRender = useIsFirstRender();
    const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
    const [isGiftCardDrawerOpen, setIsGiftCardDrawerOpen] = useState(false);
    const [rangesValue, setRangesValue] = useState<Partial<TPaymentIntentsFilter>>({
        amount_from: undefined,
        amount_to: undefined
    });
    const [debouncedRangesValue, setDebouncedRangesValue] = useDebounceValue(rangesValue, 500);

    const {
        filter,
        onChangeFilter,
        intentPayments,
        refetchIntents,
        intent,
        getSingleIntent,
        handleRemoveIntentData,
        intentsData,
        handleCancelIntent
    } = useExternalPayments({ isInitialIntents: false });

    useEffect(() => {
        if (isFirstRender) {
            refetchIntents({ isNew: true, filter });
        }
    }, []);

    const columns = [
        {
            label: 'ID',
            key: 'amount',
            render: (value: number, data: TPaymentIntent) => `$${value.toFixed(2)} ${data.currency}`
        },
        {
            label: 'Amount',
            key: 'amount',
            render: (value: number, data: TPaymentIntent) => `$${value.toFixed(2)} ${data.currency}`
        },
        {
            label: 'Status',
            key: 'status',
            render: (value: EPaymentIntentStatus) => (
                <Status variant={getIntentStatusVariant(value)}>
                    {getPaymentIntentStatusString(value)}
                </Status>
            )
        },
        {
            label: 'Created',
            key: 'created_at',
            render: (value: Date) => moment(value).format('MM.DD.YYYY HH:mm')
        },
        {
            label: 'Actions',
            key: 'id',
            render: (value: string, data: TAdminGiftCard) => (
                <Dropdown
                    items={[
                        {
                            label: 'Details',
                            onClick: () => {
                                setIsGiftCardDrawerOpen(true);
                                getSingleIntent(value);
                            }
                        },
                        {
                            label: 'Cancel',
                            onClick: () => {
                                handleCancelIntent(value);
                            }
                        }
                    ]}
                />
            )
        }
    ];

    useEffect(() => {
        setDebouncedRangesValue(rangesValue);
    }, [rangesValue]);

    useEffect(() => {
        if (!isFirstRender) {
            onChangeFilter(rangesValue);
        }
    }, [debouncedRangesValue]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <div className={styles.text_wrapper}>
                    <Typography variant="h4">External send & requests</Typography>
                    <Typography>{intentsData?.total} records found</Typography>
                </div>
                <div className={styles.actions}>
                    <Button
                        size="medium"
                        variant="primary"
                        leftIcon="filter_bars"
                        onClick={() => setIsFilterDrawerOpen(true)}>
                        Filter
                    </Button>
                </div>
            </div>
            <Table
                columns={columns}
                rows={intentPayments}
                sort={{
                    key: 'created_at',
                    value: filter.order as 'ASC' | 'DESC'
                }}
                onSortChange={(key, value) => {
                    onChangeFilter({
                        order: value
                    });
                }}
                filter={filter as TFilter}
                onLoadData={() => refetchIntents({ isNew: false, filter })}
                totalRows={intentsData?.total || 0}
                onFilterChange={(val) => onChangeFilter(val)}
                emptyDescription="External send & requests list is empty"
            />
            <Drawer
                className={styles.drawer}
                isOpen={isFilterDrawerOpen}
                onClose={() => setIsFilterDrawerOpen(false)}>
                <Typography className={styles.title} variant="h5">
                    Filter
                </Typography>
                <MultiSelect
                    full
                    items={giftCardStatuses}
                    //@ts-ignore
                    values={filter.status}
                    onChange={(val) => {
                        //@ts-ignore
                        onChangeFilter({ status: val as EGiftCardStatus });
                    }}
                    label="Status"
                />
                <RangeDatePicker
                    full
                    label="Created date range"
                    values={[
                        filter.date_from?.toString() || null,
                        filter.date_to?.toString() || null
                    ]}
                    onChange={(val) =>
                        onChangeFilter({
                            date_from: val?.[0] ? new Date(val?.[0]) : undefined,
                            date_to: val?.[1] ? new Date(val?.[1]) : undefined
                        })
                    }
                    isDisableFeature
                />
                <Typography variant="body2" className={styles.amount}>
                    Amount range
                </Typography>
                <Range
                    min={0}
                    max={1000}
                    values={[rangesValue.amount_from || 0, rangesValue.amount_to || 0]}
                    onChange={(val) =>
                        setRangesValue((state) => ({
                            ...state,
                            amount_from: val?.[0] ? Number(val[0]) : undefined,
                            amount_to: val?.[1] ? Number(val[1]) : undefined
                        }))
                    }
                />
            </Drawer>
            <Drawer
                className={styles.transaction_drawer}
                isOpen={isGiftCardDrawerOpen && !!intent}
                onClose={() => {
                    setIsGiftCardDrawerOpen(false);
                    handleRemoveIntentData();
                }}>
                <Typography className={styles.title} variant="h5">
                    Gift card details
                </Typography>
                <Input full value={intent?.id} label="Payment ID" readOnly />
                <div className={styles.row}>
                    <Input
                        full
                        value={intent ? `${intent.contact_first_name ?? ''} ${intent.contact_last_name ?? ''}`.trim() : undefined}
                        label="Contact name"
                        readOnly
                    />
                    <Input full value={intent?.contact_email} label="Contact email" readOnly />
                </div>
                <div className={styles.row}>
                    <Input full value={intent?.amount?.toFixed(2)} label="Amount" readOnly />
                    <Input full value={intent?.currency} label="Currency" readOnly />
                </div>
                <Textarea full value={intent?.message} label="Message" readOnly />
                <div className={styles.row}>
                    <Input
                        full
                        value={intent?.type === ETransactionType.CREDIT ? 'Send' : 'Request'}
                        label="Type"
                        readOnly
                    />
                    <Input
                        full
                        value={getPaymentIntentStatusString(
                            intent?.status || EPaymentIntentStatus.InProcess
                        )}
                        label="Status"
                        readOnly
                    />
                </div>
                <div className={styles.row}>
                    <Input
                        full
                        value={
                            intent?.expires_at
                                ? moment(intent?.expires_at).format('MM.DD.YYYY HH:mm')
                                : 'N/A'
                        }
                        label="Expires"
                        readOnly
                    />
                    <Input
                        full
                        value={moment(intent?.created_at).format('MM.DD.YYYY HH:mm')}
                        label="Created"
                        readOnly
                    />
                    <Input
                        full
                        value={moment(intent?.updated_at).format('MM.DD.YYYY HH:mm')}
                        label="Updated"
                        readOnly
                    />
                </div>
                {intent?.transaction && (
                    <>
                        <Typography className={styles.title} variant="h5">
                            Transaction details
                        </Typography>
                        <Input
                            full
                            value={intent?.transaction?.id}
                            label="Transaction ID"
                            readOnly
                        />
                        <div className={styles.row}>
                            <Input
                                full
                                value={intent?.transaction?.consumer_id}
                                label="Consumer ID"
                                readOnly
                            />
                            <Input
                                full
                                value={intent?.transaction?.consumer_name}
                                label="Consumer Name"
                                readOnly
                            />
                        </div>
                        <div className={styles.row}>
                            <Input
                                full
                                value={getTransactionMethod(intent?.transaction?.method)}
                                label="Method"
                                readOnly
                            />
                            <Input
                                full
                                value={getTransactionAction(intent?.transaction?.action)}
                                label="Action"
                                readOnly
                            />
                            <Input
                                full
                                value={getTransactionStatus(intent?.transaction?.status)}
                                label="Status"
                                readOnly
                            />
                        </div>
                        <div className={styles.row}>
                            <Input
                                full
                                value={intent?.transaction?.currency}
                                label="Currency"
                                readOnly
                            />
                            <Input
                                full
                                value={intent?.transaction?.amount?.toFixed(2)}
                                label="Amount"
                                readOnly
                            />
                        </div>
                        <div className={styles.row}>
                            <Input
                                full
                                value={intent?.transaction?.details?.message || '-'}
                                label="Message"
                                readOnly
                            />
                            <Input
                                full
                                value={intent?.transaction?.destination}
                                label="Destination"
                                readOnly
                            />
                        </div>
                        <div className={styles.row}>
                            <Input
                                full
                                value={intent?.transaction?.source}
                                label="Source"
                                readOnly
                            />
                            <Input
                                label="Created at"
                                full
                                value={
                                    moment(intent?.transaction?.created_at).format(
                                        'MM.DD.YYYY HH:mm'
                                    ) as string
                                }
                                readOnly
                            />
                        </div>
                    </>
                )}
            </Drawer>
        </div>
    );
};

export default ExternalPaymentsLayout;
